/// FOURJS_START_COPYRIGHT(D,2015)
/// Property of Four Js*
/// (c) Copyright Four Js 2015, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum("PageTitleWidget", ["WidgetBase", "WidgetFactory"], function (context, cls) {
  /**
   * Page title in folder widget.
   * @class PageTitleWidget
   * @memberOf classes
   * @extends classes.WidgetBase
   */
  cls.PageTitleWidget = context.oo.Class(cls.WidgetBase, function ($super) {
    return /** @lends classes.PageTitleWidget.prototype */ {
      __name: "PageTitleWidget",

      /** @type {classes.ImageWidget} */
      _image: null,
      /** @type {HTMLElement} */
      _titleElement: null,
      /** @type {HTMLElement} */
      _actionsContainerElement: null,
      /** @type {boolean} */
      _accordionMode: false,
      /** whether this tab is the current roving Tab stop (the "aimed" tab). @type {boolean} */
      _aimed: false,

      /**
       * @inheritDoc
       */
      _initElement: function () {
        this._ignoreLayout = true;
        $super._initElement.call(this);
        this._titleElement = this._element.getElementsByClassName("gbc_PageTitleWidget__text")[0];
        this._actionsContainerElement = this._element.getElementsByClassName("gbc_PageTitleWidget__actions")[0];
        // Seed the roving tabindex: a non-current tab leaves the Tab order in
        // accessibility mode; setCurrent aims the selected one.
        this._updateRovingTabIndex();
      },

      /**
       * @inheritDoc
       */
      _initLayout: function () {
        this._layoutInformation = new cls.PageTitleLayoutInformation(this);
        // no layout
      },

      /**
       * @inheritDoc
       */
      destroy: function () {
        this._actionsContainerElement = null;
        this._titleElement = null;
        if (this._image) {
          this._image.destroy();
          this._image = null;
        }
        $super.destroy.call(this);
      },

      /**
       * @inheritDoc
       */
      manageMouseClick: function (domEvent) {
        this.emit(context.constants.widgetEvents.click, domEvent);
        return $super.manageMouseClick.call(this, domEvent);
      },

      /**
       * Tablist/accordion keyboard model. This handler is only reached when the tab is
       * the DOM-focused widget, which KeyboardService routes here exclusively in
       * accessibility mode — so no explicit mode guard is needed (same reliance on
       * routing as SplitterWidget):
       *  - Space/Enter activate the focused tab / toggle the focused accordion header,
       *  - Left/Right (RTL-aware) move focus between TABLIST tabs (manual activation —
       *    the active page is not switched; the folder wraps around). An accordion keeps
       *    every header in the Tab order, so per the WAI-ARIA Accordion pattern it has no
       *    arrow-key navigation.
       * @inheritDoc
       */
      managePriorityKeyDown: function (keyString, domKeyEvent, repeat) {
        if (keyString === "space" || keyString === "return" || keyString === "enter") {
          this.emit(context.constants.widgetEvents.tabActivate);
          return true;
        }
        if (!this._accordionMode) {
          if (keyString === this.getStart()) {
            this.emit(context.constants.widgetEvents.tabNavigate, -1);
            return true;
          }
          if (keyString === this.getEnd()) {
            this.emit(context.constants.widgetEvents.tabNavigate, 1);
            return true;
          }
          // Home/End are optional in the WAI-ARIA APG Tabs pattern: focus the
          // first / last tab (manual activation, like the arrows).
          if (keyString === "home") {
            this.emit(context.constants.widgetEvents.tabNavigate, "first");
            return true;
          }
          if (keyString === "end") {
            this.emit(context.constants.widgetEvents.tabNavigate, "last");
            return true;
          }
        }
        return $super.managePriorityKeyDown.call(this, keyString, domKeyEvent, repeat);
      },

      /**
       * Set the page title text
       * @param {string} text the text to display
       * @publicdoc
       */
      setText: function (text) {
        if (text === null) {
          return;
        }
        const isEmpty = text.trim().length === 0;
        this.toggleClass("gbc_loading", isEmpty);

        const title = (isEmpty ? gbc.I18NService.t("gwc.main.tabbedContainer.loading") : text).replaceAll(/&(.)/g, "$1");
        this._setTextContent(title, "_titleElement");
        if (!this.getTitle()) {
          // fallback if no title defined
          this.setTitle(title);
        }
      },

      /**
       * Set the dom title of the pageTitle widget
       * @param {string} title - text to display as title
       */
      setTitle: function (title) {
        this.domAttributesMutator(
          function () {
            this._titleElement.title = title;
          }.bind(this)
        );
      },

      /**
       * Get the dom title of the pageTitle widget
       * @return {string} text displayed as title
       */
      getTitle: function () {
        return this._titleElement.title;
      },

      /**
       * Get the page title text
       * @returns {string} the text displayed
       * @publicdoc
       */
      getText: function () {
        return this._titleElement.textContent;
      },

      /**
       * Get the action container element
       * @return {HTMLElement} the action container
       */
      getActionsContainerElement: function () {
        return this._actionsContainerElement;
      },

      /**
       * Define an image to display next to the title
       * @param {string} image the URL of the displayed image or a font-image URL: font:[fontname]:[character]:[color]
       * @publicdoc
       */
      setImage: function (image) {
        if (!this._image) {
          const opts = this.getBuildParameters();
          opts.ignoreLayout = true;
          this._image = cls.WidgetFactory.createWidget("Image", opts);
          cls.DOMElementHelper.prependChild(this._element, this._image.getElement());
        }
        this._image.setHidden(true);
        if (image && image !== "") {
          this._image.setSrc(image);
          this._image.setHidden(false);
          if (!this._image.getTitle()) {
            this.domAttributesMutator(
              function () {
                this._image.setTitle(this._titleElement.title);
              }.bind(this)
            );
          }
        }
      },

      /**
       * Get the image displayed next to the title
       * @returns {?string} the URL of the displayed image or a font-image URL: font:[fontname]:[character]:[color]
       * @publicdoc
       */
      getImage: function () {
        if (this._image) {
          return this._image.getSrc();
        }
        return null;
      },

      /**
       * Set the tab to be the current one
       * @param {boolean} current displays the current tab indicator
       * @publicdoc
       */
      setCurrent: function (current) {
        this.toggleClass("gbc_isCurrent", current);
        const ariaState = String(Boolean(current));
        if (this._accordionMode) {
          // accordion header pattern: aria-expanded reflects panel visibility
          this.setAriaAttribute("expanded", ariaState);
        } else {
          // tablist tab pattern: aria-selected reflects active tab
          this.setAriaAttribute("selected", ariaState);
        }
        // The selected tab is the default Tab stop of the tablist (roving aim
        // follows selection until arrow navigation moves it — accessibility mode).
        this.setAimed(current);
      },

      /**
       * Set whether this tab is the "aimed" tab: the single Tab stop of the
       * tablist under the WAI-ARIA roving tabindex model. Selection aims the tab
       * by default (see setCurrent); in accessibility mode the arrow keys move
       * the aim between tabs without changing the active page (manual activation).
       * @param {boolean} aimed
       */
      setAimed: function (aimed) {
        this._aimed = Boolean(aimed);
        this._updateRovingTabIndex();
      },

      /**
       * Apply the roving tabindex to this tab. Tablist tabs use the WAI-ARIA roving
       * model: only the aimed tab stays in the Tab order (tabindex="0"), the others
       * get tabindex="-1" and are reached with the arrow keys. This is exposed
       * unconditionally — KeyboardService alone decides, per accessibility mode,
       * whether the browser Tab order is actually used; outside the
       * mode GBC intercepts Tab, so the "-1" tabs are harmless. Accordion headers are
       * each an independent Tab stop (no roving): they always keep tabindex="0".
       * @private
       */
      _updateRovingTabIndex: function () {
        if (!this._element) {
          return;
        }
        const value = this._accordionMode || this._aimed ? "0" : "-1";
        if (this._element.getAttribute("tabindex") !== value) {
          this._element.setAttribute("tabindex", value);
        }
      },

      /**
       * Give the DOM focus to this tab's root element. Used by the folder's
       * roving navigation (arrow keys) and tab activation in accessibility mode.
       */
      focusTab: function () {
        if (this._element) {
          this._element.focus();
        }
      },

      /**
       * Switch the title's ARIA semantics between tablist tab (default: role="tab" + aria-selected)
       * and accordion header (role="button" + aria-expanded). Initial state is computed from
       * isCurrent() so a page that's already current keeps its current state across the switch.
       * @param {boolean} accordion true for accordion header semantics, false for tablist tab
       */
      setAccordionMode: function (accordion) {
        this._accordionMode = Boolean(accordion);
        const ariaState = String(this.isCurrent());
        if (this._accordionMode) {
          this.setAriaRole("button");
          this.setAriaAttribute("selected", null);
          this.setAriaAttribute("expanded", ariaState);
        } else {
          this.setAriaRole("tab");
          this.setAriaAttribute("expanded", null);
          this.setAriaAttribute("selected", ariaState);
        }
        // Accordion headers are each independently Tab-focusable; tablist tabs
        // use roving. Re-apply now that the mode is known.
        this._updateRovingTabIndex();
      },

      /**
       * Check if the tab is the current one
       * @returns {boolean} true if the current tab indicator is displayed
       * @publicdoc
       */
      isCurrent: function () {
        return this._element.classList.contains("gbc_isCurrent");
      },

      /**
       * Define the position of the collapser icon
       * @param {string} pos - could be 'left' or 'right' (default)
       */
      setCollapserPosition: function (pos) {
        this.addClasses("collapser-position-" + pos);
      }
    };
  });
  cls.WidgetFactory.registerBuilder("PageTitle", cls.PageTitleWidget);
});
